<template>
  <div class="formulario-modelo">
    <div class="header-flex">
      <h1>Autoevaluación Institucional</h1>
      <button v-if="modoResultadoDirecto" class="btn salir" @click="volver">Volver</button>
    </div>
    <p class="subtitulo">
      Consolidado de resultados de múltiples campus bajo un mismo proceso institucional.
    </p>
    <div class="divider"></div>

    <!-- ── Modo resultado directo (viene con ?id=) ── -->
    <template v-if="modoResultadoDirecto">
      <div v-if="cargandoDirecto" class="loading">Cargando proceso...</div>
      <template v-else-if="procesoActivo">
        <div class="res-cab">
          <div>
            <h2 class="res-titulo">{{ procesoActivo.nombre }}</h2>
            <span class="res-periodo">{{ procesoActivo.periodo }}</span>
          </div>
          <div class="filtro-campus">
            <label>Ver campus:</label>
            <select v-model="campusFiltrado" class="form-control" @change="cargarResultados">
              <option :value="null">Todos (consolidado)</option>
              <option v-for="e in procesoActivo.ejecuciones ?? []" :key="e.id_ejecucion" :value="e.id_campus">
                {{ e.campus?.nombre ?? 'Sin campus' }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="cargandoResultados" class="loading">Calculando resultados...</div>
        <template v-else-if="resultados">
          <div v-if="resultados.consolidado" class="consolidado">
            <div class="cons-cab">
              <div class="cons-nota-bloque">
                <span class="cons-etiqueta">Resultado institucional</span>
                <span class="cons-nota">{{ resultados.consolidado.global?.toFixed(2) ?? '—' }}</span>
                <span class="cons-escala">sobre {{ resultados.consolidado.escala_max }}</span>
              </div>
              <span
                v-if="resultados.consolidado.equivalencias?.en_diez != null && resultados.consolidado.escala_max === 5"
                class="cons-equivalente"
              >
                equivale a {{ resultados.consolidado.equivalencias.en_diez?.toFixed(2) }} en escala 1–10
              </span>
            </div>
            <p class="cons-detalle">
              Consolidado <strong>{{ resultados.criterio_consolidacion }}</strong>
              sobre {{ resultados.consolidado.campus_considerados }} campus.
            </p>
            <p v-if="resultados.consolidado.campus_sin_calificar.length" class="cons-aviso">
              Sin calificar todavía:
              {{ resultados.consolidado.campus_sin_calificar.map(c => c.campus ?? 'sin nombre').join(', ') }}.
              No cuentan como cero: quedan fuera del cálculo.
            </p>
            <div class="tabla-scroll" v-if="resultados.consolidado.factores.length">
              <table class="tabla">
                <thead>
                  <tr><th>Factor</th><th>Nota consolidada</th><th>Campus con nota</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(f, i) in resultados.consolidado.factores" :key="i">
                    <td>{{ f.ordinal }} {{ f.nombre }}</td>
                    <td>{{ f.calificacion?.toFixed(2) ?? '—' }}</td>
                    <td>{{ f.campus_con_nota }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <h4 class="sub-titulo">Por campus</h4>
          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr><th>Campus</th><th>Estado</th><th>Peso</th><th>Global</th><th>Nivel</th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="c in resultados.campus" :key="c.id_ejecucion">
                  <td>{{ c.campus ?? 'Sin campus' }}</td>
                  <td><span :class="['estado', estadoClass(estadosPorEjecucion[c.id_ejecucion])]">{{ estadosPorEjecucion[c.id_ejecucion] ?? '—' }}</span></td>
                  <td>{{ c.peso }}</td>
                  <td>{{ c.resultados?.global?.valoracion?.toFixed(2) ?? '—' }}</td>
                  <td>{{ c.resultados?.global?.grado ?? 'Sin calificar' }}</td>
                  <td>
                    <button
                      class="btn guardar btn-tabla"
                      :disabled="estadosPorEjecucion[c.id_ejecucion] !== 'informe'"
                      @click="irAResultadosCampus(c.id_ejecucion)"
                    >Resultados</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <p v-else class="texto-vacio">Los resultados no están disponibles aún.</p>
      </template>
      <p v-else class="texto-vacio">No se encontró el proceso solicitado.</p>
    </template>

    <!-- ── Modo lista completa (acceso normal desde el menú) ── -->
    <template v-else>
      <div class="tabla-scroll">
        <div v-if="cargando" class="loading">Cargando procesos institucionales...</div>
        <table v-else-if="procesos.length" class="tabla">
          <thead>
            <tr>
              <th>Proceso</th>
              <th>Período</th>
              <th>Versión</th>
              <th>Campus</th>
              <th>Consolidación</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in procesos" :key="p.id_proceso_institucional">
              <td>{{ p.nombre }}</td>
              <td>{{ p.periodo }}</td>
              <td>{{ p.version?.nombre ?? '—' }}</td>
              <td>
                <span class="chip-campus" v-for="e in p.ejecuciones ?? []" :key="e.id_ejecucion">
                  {{ e.campus?.nombre ?? 'Sin campus' }}
                </span>
                <span v-if="!(p.ejecuciones ?? []).length" class="texto-vacio">Sin campus</span>
              </td>
              <td><span class="badge">{{ p.criterio_consolidacion }}</span></td>
              <td>
                <button class="btn guardar btn-tabla" @click="verResultados(p)">Ver resultados</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else-if="!cargando" class="texto-vacio">Todavía no hay procesos institucionales.</p>
      </div>

      <div v-if="!mostrarForm" class="botones" style="margin: 1rem 0 1.5rem;">
        <button class="btn guardar" @click="abrirFormulario">Nuevo Proceso Institucional</button>
      </div>

      <div v-if="mostrarForm" class="marco">
        <h3 class="form-titulo">Nuevo Proceso Institucional</h3>

        <div class="formulario-opcion">
          <span>Nombre <b class="req">*</b></span>
          <input v-model="form.nombre" class="form-control" placeholder="Autoevaluación Institucional 2026" />
        </div>

        <div class="fila-doble">
          <div class="formulario-opcion">
            <span>Período <b class="req">*</b></span>
            <div class="periodo-editor">
              <input type="number" v-model.number="periodoAnio" class="periodo-anio" min="2000" step="1" />
              <span class="periodo-sep">-</span>
              <select v-model="periodoSemestre" class="periodo-semestre">
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
            </div>
          </div>
          <div class="formulario-opcion">
            <span>Versión del modelo <b class="req">*</b></span>
            <select v-model="form.id_version" class="form-control">
              <option :value="0" disabled>Selecciona una versión</option>
              <option v-for="v in versiones" :key="v.id_version" :value="v.id_version">
                {{ v.nombre }}
              </option>
            </select>
          </div>
        </div>

        <div class="formulario-opcion">
          <span>Campus participantes <b class="req">*</b></span>
          <p class="ayuda">
            Cada campus marcado recibe su propia ejecución del modelo.
          </p>
          <div class="campus-grid">
            <label v-for="c in campusDisponibles" :key="c.id_campus" class="campus-item">
              <input type="checkbox" :value="c.id_campus" v-model="form.campus" />
              <span class="campus-nombre">{{ c.nombre }}</span>
              <small class="campus-programas">{{ c.programas }} programa{{ c.programas === 1 ? '' : 's' }}</small>
            </label>
          </div>
          <p v-if="!campusDisponibles.length" class="texto-vacio">
            No hay campus registrados.
          </p>
        </div>

        <div class="formulario-opcion">
          <span>Criterio de consolidación</span>
          <select v-model="form.criterio_consolidacion" class="form-control">
            <option value="ponderado">Ponderado por número de programas</option>
            <option value="simple">Promedio simple (cada campus pesa igual)</option>
          </select>
        </div>

        <div class="formulario-opcion">
          <span>Observaciones <span class="opcional">(opcional)</span></span>
          <textarea v-model="form.observaciones" class="form-control" rows="2"></textarea>
        </div>

        <div class="botones">
          <button class="btn salir" @click="mostrarForm = false">Cancelar</button>
          <button class="btn guardar" :disabled="!puedeGuardar || guardando" @click="guardar">
            {{ guardando ? 'Creando...' : 'Crear proceso' }}
          </button>
        </div>
      </div>

      <!-- Panel de resultados al seleccionar desde la lista -->
      <div v-if="procesoActivo" class="marco resultados">
        <div class="res-cab">
          <h3 class="res-titulo-sm">{{ procesoActivo.nombre }} · {{ procesoActivo.periodo }}</h3>
          <button class="btn salir btn-tabla" @click="cerrarResultados">Cerrar</button>
        </div>
        <div class="filtro-campus">
          <label>Ver campus:</label>
          <select v-model="campusFiltrado" class="form-control filtro-select" @change="cargarResultados">
            <option :value="null">Todos los campus (consolidado)</option>
            <option v-for="e in procesoActivo.ejecuciones ?? []" :key="e.id_ejecucion" :value="e.id_campus">
              {{ e.campus?.nombre ?? 'Sin campus' }}
            </option>
          </select>
        </div>
        <div v-if="cargandoResultados" class="loading">Calculando resultados...</div>
        <template v-else-if="resultados">
          <div v-if="resultados.consolidado" class="consolidado">
            <div class="cons-cab">
              <div class="cons-nota-bloque">
                <span class="cons-etiqueta">Resultado institucional</span>
                <span class="cons-nota">{{ resultados.consolidado.global?.toFixed(2) ?? '—' }}</span>
                <span class="cons-escala">sobre {{ resultados.consolidado.escala_max }}</span>
              </div>
              <span
                v-if="resultados.consolidado.equivalencias?.en_diez != null && resultados.consolidado.escala_max === 5"
                class="cons-equivalente"
              >
                equivale a {{ resultados.consolidado.equivalencias.en_diez?.toFixed(2) }} en escala 1–10
              </span>
            </div>
            <p class="cons-detalle">
              Consolidado <strong>{{ resultados.criterio_consolidacion }}</strong>
              sobre {{ resultados.consolidado.campus_considerados }} campus.
            </p>
            <p v-if="resultados.consolidado.campus_sin_calificar.length" class="cons-aviso">
              Sin calificar todavía:
              {{ resultados.consolidado.campus_sin_calificar.map(c => c.campus ?? 'sin nombre').join(', ') }}.
            </p>
            <div class="tabla-scroll" v-if="resultados.consolidado.factores.length">
              <table class="tabla">
                <thead>
                  <tr><th>Factor</th><th>Nota consolidada</th><th>Campus con nota</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(f, i) in resultados.consolidado.factores" :key="i">
                    <td>{{ f.ordinal }} {{ f.nombre }}</td>
                    <td>{{ f.calificacion?.toFixed(2) ?? '—' }}</td>
                    <td>{{ f.campus_con_nota }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <h4 class="sub-titulo">Por campus</h4>
          <div class="tabla-scroll">
            <table class="tabla">
              <thead>
                <tr><th>Campus</th><th>Estado</th><th>Peso</th><th>Global</th><th>Nivel</th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="c in resultados.campus" :key="c.id_ejecucion">
                  <td>{{ c.campus ?? 'Sin campus' }}</td>
                  <td><span :class="['estado', estadoClass(estadosPorEjecucion[c.id_ejecucion])]">{{ estadosPorEjecucion[c.id_ejecucion] ?? '—' }}</span></td>
                  <td>{{ c.peso }}</td>
                  <td>{{ c.resultados?.global?.valoracion?.toFixed(2) ?? '—' }}</td>
                  <td>{{ c.resultados?.global?.grado ?? 'Sin calificar' }}</td>
                  <td>
                    <button
                      class="btn guardar btn-tabla"
                      :disabled="estadosPorEjecucion[c.id_ejecucion] !== 'informe'"
                      @click="irAResultadosCampus(c.id_ejecucion)"
                    >Resultados</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </template>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import { useGestionVersiones } from '@/store/GestionVersiones'
import {
  listarProcesosInstitucionalesService,
  getCampusDisponiblesService,
  crearProcesoInstitucionalService,
  getResultadosInstitucionalesService,
  getProcesoInstitucionalService,
  type ProcesoInstitucional,
  type CampusDisponible,
  type ResultadosInstitucionales
} from '@/services/ProcesoInstitucionalService'

const route = useRoute()
const router = useRouter()
const versionesStore = useGestionVersiones()

const modoResultadoDirecto = computed(() => !!route.query.id)

const procesos = ref<ProcesoInstitucional[]>([])
const campusDisponibles = ref<CampusDisponible[]>([])
const versiones = ref<Array<{ id_version: number; nombre: string }>>([])
const cargando = ref(false)
const guardando = ref(false)
const mostrarForm = ref(false)
const cargandoDirecto = ref(false)

const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error' = 'positivo') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const hoy = new Date()
const periodoAnio = ref(hoy.getFullYear())
const periodoSemestre = ref(hoy.getMonth() < 6 ? '1' : '2')

const form = ref({
  nombre: '',
  id_version: 0,
  criterio_consolidacion: 'ponderado' as 'simple' | 'ponderado',
  observaciones: '',
  campus: [] as number[]
})

const puedeGuardar = computed(
  () => !!form.value.nombre.trim() && form.value.id_version > 0 && form.value.campus.length > 0
)

function abrirFormulario() {
  form.value = { nombre: '', id_version: 0, criterio_consolidacion: 'ponderado', observaciones: '', campus: [] }
  mostrarForm.value = true
}

async function cargar() {
  cargando.value = true
  try {
    procesos.value = await listarProcesosInstitucionalesService()
    campusDisponibles.value = await getCampusDisponiblesService()
  } finally {
    cargando.value = false
  }
}

async function guardar() {
  if (!puedeGuardar.value) return
  guardando.value = true
  try {
    const resultado = await crearProcesoInstitucionalService({
      nombre: form.value.nombre.trim(),
      periodo: `${periodoAnio.value}-${periodoSemestre.value}`,
      id_version: form.value.id_version,
      criterio_consolidacion: form.value.criterio_consolidacion,
      observaciones: form.value.observaciones || null,
      campus: form.value.campus
    })
    if (!resultado.ok) {
      mostrarMensaje(resultado.error ?? 'No se pudo crear el proceso', 'error')
      return
    }
    mostrarMensaje(`Proceso creado con ${form.value.campus.length} campus`, 'positivo')
    mostrarForm.value = false
    await cargar()
  } finally {
    guardando.value = false
  }
}

// ── Resultados ────────────────────────────────────────────────────────
const procesoActivo = ref<ProcesoInstitucional | null>(null)
const resultados = ref<ResultadosInstitucionales | null>(null)
const campusFiltrado = ref<number | null>(null)
const cargandoResultados = ref(false)

const estadosPorEjecucion = computed<Record<number, string>>(() => {
  const mapa: Record<number, string> = {}
  for (const e of procesoActivo.value?.ejecuciones ?? []) {
    mapa[e.id_ejecucion] = e.estado
  }
  return mapa
})

function irAResultadosCampus(idEjecucion: number) {
  router.push({ name: 'ResultadosAutoevaluacion', params: { id_ejecucion: idEjecucion } })
}

function estadoClass(estado?: string | null) {
  const map: Record<string, string> = {
    preparacion: 'estado-modificar',
    ejecucion:   'estado-completado',
    evaluacion:  'estado-aprobado',
    informe:     'estado-aprobado',
    finalizado:  'estado-descontinuado'
  }
  return map[estado ?? ''] ?? 'estado-modificar'
}

async function verResultados(proceso: ProcesoInstitucional) {
  procesoActivo.value = proceso
  campusFiltrado.value = null
  await cargarResultados()
}

async function cargarResultados() {
  if (!procesoActivo.value) return
  cargandoResultados.value = true
  try {
    resultados.value = await getResultadosInstitucionalesService(
      procesoActivo.value.id_proceso_institucional,
      campusFiltrado.value
    )
  } finally {
    cargandoResultados.value = false
  }
}

function cerrarResultados() {
  procesoActivo.value = null
  resultados.value = null
}

function volver() {
  router.push({ name: 'ProcesoAutoevaluacion', query: { modo: 'admin', tab: 'institucional-general' } })
}

onMounted(async () => {
  await versionesStore.getVersiones()
  versiones.value = (versionesStore.versiones as any[]).map(v => ({
    id_version: v.id_version,
    nombre: v.nombre
  }))

  const idParam = route.query.id
  if (idParam) {
    cargandoDirecto.value = true
    const id = Number(idParam)
    const proceso = await getProcesoInstitucionalService(id)
    cargandoDirecto.value = false
    if (proceso) {
      await verResultados(proceso)
    }
  } else {
    await cargar()
  }
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-width: 0;
  overflow-x: hidden;
}

.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

h1 {
  color: #111;
  font-size: 1.3rem;
  margin: 0;
}

.subtitulo { color: #5a6b7c; font-size: 0.9rem; margin: 0.3rem 0 0; }
.divider { height: 1px; background: #e0e0e0; margin: 1rem 0 1.5rem; }
.loading { padding: 1rem 0; color: #555; font-size: 0.88rem; }
.texto-vacio { color: #9aa5b1; font-size: 0.82rem; font-style: italic; }

/* Tablas responsivas */
.tabla-scroll { width: 100%; overflow-x: auto; margin-bottom: 1rem; }
.tabla { width: 100%; border-collapse: collapse; min-width: 420px; }
.tabla th {
  text-align: left;
  padding: 0.55rem 0.7rem;
  background: #0e69a0;
  color: white;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.estado { padding: 4px 10px; border-radius: 12px; font-size: 0.8rem; font-weight: 600; display: inline-block; letter-spacing: 0.03em; text-transform: capitalize; }
.estado-modificar     { background-color: #eea222; color: #000; }
.estado-aprobado      { background-color: #d1f3d1; color: #1a7f1a; }
.estado-completado    { background-color: #0e69a0; color: white; }
.estado-descontinuado { background-color: #e0e0e0; color: #666; }
.tabla td {
  padding: 0.5rem 0.7rem;
  border-bottom: 1px solid #eef2f6;
  font-size: 0.85rem;
}

/* Formulario */
.marco {
  border: 1px solid #e0e0e0;
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}
.form-titulo { margin: 0 0 1rem; color: #111; font-size: 1rem; font-weight: 700; }
.formulario-opcion { margin-bottom: 1rem; display: flex; flex-direction: column; gap: 0.35rem; }
.formulario-opcion > span { font-weight: 600; font-size: 0.85rem; color: #374a5c; }
.fila-doble { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.req { color: #c0392b; }
.opcional { font-weight: 400; font-size: 0.78rem; color: #9aa5b1; }
.ayuda { font-size: 0.78rem; color: #6b7280; margin: 0; }
.form-control {
  padding: 0.5rem 0.6rem;
  border: 1px solid #d6dee6;
  border-radius: 6px;
  font-size: 0.86rem;
  width: 100%;
  box-sizing: border-box;
}
.periodo-editor { display: flex; align-items: center; gap: 0.4rem; }
.periodo-anio { width: 6rem; padding: 0.5rem; border: 1px solid #d6dee6; border-radius: 6px; }
.periodo-semestre { padding: 0.5rem; border: 1px solid #d6dee6; border-radius: 6px; }
.periodo-sep { color: #9aa5b1; }

.campus-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.6rem; }
.campus-item {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.55rem 0.7rem; border: 1px solid #d6dee6;
  border-radius: 8px; cursor: pointer; background: #fbfdff;
}
.campus-item:hover { background: #f2f8fd; }
.campus-nombre { font-size: 0.86rem; color: #24303c; }
.campus-programas { margin-left: auto; font-size: 0.72rem; color: #9aa5b1; }

.chip-campus {
  display: inline-block; padding: 2px 8px; margin: 0 4px 4px 0;
  border-radius: 999px; background: #eef5fb; color: #0e69a0;
  font-size: 0.75rem; font-weight: 600;
}
.badge {
  display: inline-block; padding: 3px 8px; border-radius: 4px;
  background: #e8f4fd; color: #0e69a0; font-weight: 600; font-size: 0.78rem;
}

/* Botones */
.botones { display: flex; gap: 0.6rem; justify-content: flex-end; flex-wrap: wrap; }
.btn {
  padding: 0.5rem 1rem; border: none; border-radius: 6px;
  cursor: pointer; font-size: 0.85rem; font-weight: 600;
  text-decoration: none; display: inline-flex; align-items: center;
}
.btn.guardar { background: #eea222; color: black; }
.btn.guardar:hover:not(:disabled) { background: #0e69a0; color: white; }
.btn.guardar:disabled { opacity: 0.45; cursor: not-allowed; }
.btn.salir { background: #eceff3; color: #4a5b6b; }
.btn.salir:hover { background: #dde3ea; }
.btn-tabla { padding: 0.3rem 0.75rem; font-size: 0.8rem; }

/* Resultados */
.resultados { background: #fbfdff; }
.res-cab {
  display: flex; justify-content: space-between; align-items: flex-start;
  flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1rem;
}
.res-titulo { margin: 0; color: #111; font-size: 1.05rem; font-weight: 700; }
.res-titulo-sm { margin: 0; color: #111; font-size: 0.95rem; font-weight: 700; }
.res-periodo { font-size: 0.82rem; color: #6b7280; }
.filtro-campus { display: flex; align-items: center; gap: 0.6rem; margin-bottom: 1rem; flex-wrap: wrap; }
.filtro-campus label { font-size: 0.82rem; color: #4a5b6b; white-space: nowrap; }
.filtro-select { max-width: 22rem; }

.consolidado {
  border: 1px solid #cfe6d4; background: #f4fbf6;
  border-radius: 8px; padding: 1rem; margin-bottom: 1.2rem;
}
.cons-cab {
  display: flex; justify-content: space-between; align-items: baseline;
  flex-wrap: wrap; gap: 0.6rem;
}
.cons-nota-bloque { display: flex; align-items: baseline; gap: 0.35rem; flex-wrap: wrap; }
.cons-etiqueta { font-size: 0.8rem; color: #4a5b6b; }
.cons-nota { font-size: 1.8rem; font-weight: 800; color: #1d7a3a; }
.cons-escala { font-size: 0.8rem; color: #6b7280; }
.cons-equivalente {
  font-size: 0.8rem; color: #0e69a0; background: #eef5fb;
  padding: 3px 9px; border-radius: 999px; white-space: nowrap;
}
.cons-detalle { margin: 0.5rem 0 0; font-size: 0.82rem; color: #4a5b6b; }
.cons-aviso { margin: 0.4rem 0 0; font-size: 0.8rem; color: #b06000; }
.sub-titulo { margin: 1.2rem 0 0.5rem; font-size: 0.9rem; color: #374a5c; font-weight: 700; }

@media (max-width: 640px) {
  .formulario-modelo { padding: 1rem; }
  .fila-doble { grid-template-columns: 1fr; }
  .campus-grid { grid-template-columns: 1fr; }
  .cons-nota { font-size: 1.4rem; }
  .filtro-select { max-width: 100%; width: 100%; }
  .res-cab { flex-direction: column; }
  .marco { padding: 1rem; }
}
</style>
